import { supabase } from '../lib/supabase';
import { Player } from '../types';

export const getPlayers = async (): Promise<Player[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const getPlayer = async (id: number): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single();
  
  if (error) throw error;
  return data;
};

export const getPlayerByUUID = async (uuid: string): Promise<Player | null> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('playerUUID', uuid)
    .is('deleted_at', null)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  return data || null;
};

export const createPlayer = async (player: Omit<Player, 'id' | 'created_at'>): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .insert(player)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updatePlayer = async (id: number, updates: Partial<Player>): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .update(updates)
    .eq('id', id)
    .is('deleted_at', null)
    .select();
  
  if (error) throw error;
  
  // Return first row if available, otherwise fetch the player separately
  if (data && data.length > 0) {
    return data[0];
  }
  
  // If no data returned (RLS blocked SELECT), fetch it separately
  const { data: player, error: fetchError } = await supabase
    .from('players')
    .select('*')
    .eq('id', id)
    .single();
  
  if (fetchError) throw fetchError;
  return player;
};

// Soft delete functions
export const softDeletePlayer = async (userUuid: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('soft_delete_player', {
    p_user_uuid: userUuid
  });
  
  if (error) {
    console.error('Error soft deleting player:', error);
    throw error;
  }
  
  return data;
};

export const restorePlayer = async (userUuid: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('restore_player', {
    p_user_uuid: userUuid
  });
  
  if (error) {
    console.error('Error restoring player:', error);
    throw error;
  }
  
  return data;
};

export const isPlayerDeleted = async (userUuid: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('is_player_deleted', {
    p_user_uuid: userUuid
  });
  
  if (error) {
    console.error('Error checking if player is deleted:', error);
    throw error;
  }
  
  return data;
};

// Find deleted player by email for restoration
export const findDeletedPlayerByEmail = async (email: string): Promise<Player | null> => {
  const { data, error } = await supabase.rpc('find_deleted_player_by_email', {
    p_email: email
  });
  
  if (error) {
    console.error('Error finding deleted player by email:', error);
    throw error;
  }
  
  return data && data.length > 0 ? data[0] : null;
};

// Restore player during sign-up
export const restorePlayerOnSignup = async (oldPlayerUuid: string, newPlayerUuid: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('restore_player_on_signup', {
    p_old_player_uuid: oldPlayerUuid,
    p_new_player_uuid: newPlayerUuid
  });
  
  if (error) {
    console.error('Error restoring player on signup:', error);
    throw error;
  }
  
  return data;
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 👤 GUEST PLAYERS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Creates a guest player (no auth.users connection)
 * Guest players can play games but have no login credentials
 * Email is optional but recommended for later account linking
 */
export const createGuestPlayer = async (
  name: string,
  preName?: string,
  email?: string
): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .insert({
      name,
      preName: preName || null,
      email: email || null,
      is_guest: true,
      playerUUID: null,
      pin: null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating guest player:', error);
    throw error;
  }
  
  return data;
};

/**
 * Finds existing guest players by name (for reuse)
 * Returns up to 5 most recent matching guests
 */
export const findGuestPlayers = async (
  name: string,
  preName?: string
): Promise<Player[]> => {
  const { data, error } = await supabase.rpc('find_guest_player', {
    p_name: name,
    p_prename: preName || null
  });

  if (error) {
    console.error('Error finding guest players:', error);
    throw error;
  }

  return data || [];
};

/**
 * Gets all guest players (for admin/cleanup)
 */
export const getAllGuestPlayers = async (): Promise<Player[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('is_guest', true)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error getting guest players:', error);
    throw error;
  }

  return data || [];
};

/**
 * Finds a guest player by email address
 * Used during sign-up to check if user already exists as guest
 */
export const findGuestByEmail = async (email: string): Promise<Player | null> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('is_guest', true)
    .eq('email', email.toLowerCase().trim())
    .is('deleted_at', null)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error finding guest by email:', error);
    throw error;
  }

  return data || null;
};

/**
 * Converts a guest player to a registered player
 * Used when a guest decides to create an account
 */
export const convertGuestToPlayer = async (
  guestId: number,
  playerUUID: string,
  email: string,
  pin: string
): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .update({
      is_guest: false,
      playerUUID,
      email,
      pin
    })
    .eq('id', guestId)
    .eq('is_guest', true)
    .select()
    .single();

  if (error) {
    console.error('Error converting guest to player:', error);
    throw error;
  }

  return data;
};

